import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BracesIcon, PlusIcon, TrashIcon, AlertTriangleIcon, CheckIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getVariableUsage } from '../../utils/variables';
import type { NoticeVariable } from '../../types/variables';
import type { ChannelsState } from '../../types/composer';
interface VariablesBlockProps {
  variables: NoticeVariable[];
  onChange: (variables: NoticeVariable[]) => void;
  channels: ChannelsState;
  /** Language the notice content is being written in. */
  contentLang: 'fr' | 'en';
}
export function VariablesBlock({
  variables,
  onChange,
  channels,
  contentLang
}: VariablesBlockProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [creating, setCreating] = useState(false);
  const [draftLabel, setDraftLabel] = useState('');
  const [draftFr, setDraftFr] = useState('');
  const [draftEn, setDraftEn] = useState('');
  const [blocked, setBlocked] = useState<string | null>(null);

  // System variables stay available in the "{ }" picker, but are not listed
  // here — they need no configuration from the author.
  const customVariables = variables.filter(v => v.kind === 'custom');
  const patch = (id: string, next: Partial<NoticeVariable>) => onChange(variables.map(v => v.id === id ? {
    ...v,
    ...next
  } : v));
  const createVariable = (e: React.FormEvent) => {
    e.preventDefault();
    const label = draftLabel.trim();
    if (!label) return;
    onChange([...variables, {
      id: `custom.${Date.now()}`,
      label: {
        fr: label,
        en: label
      },
      kind: 'custom',
      value: {
        fr: draftFr.trim(),
        en: draftEn.trim()
      }
    }]);
    setDraftLabel('');
    setDraftFr('');
    setDraftEn('');
    setCreating(false);
  };
  const removeVariable = (variable: NoticeVariable) => {
    const usage = getVariableUsage(variable.id, channels, lang);
    if (usage.count > 0) {
      setBlocked(variable.id);
      window.setTimeout(() => setBlocked(null), 6000);
      return;
    }
    onChange(variables.filter(v => v.id !== variable.id));
  };
  return <section className="space-y-6" data-id="element-4177" data-mp="2ubrkfqsvtgpq">
      <h2 className="text-xl font-semibold text-slate-900" data-id="element-4178" data-mp="2z88awrnk075z">
        {t('Variables', 'Variables')}
      </h2>

      <div className="bg-white rounded-xl border-[0.5px] border-slate-200 p-6 space-y-6" data-id="element-4179" data-mp="33amsl8fhl0vo">
        {/* Explanation */}
        <div className="flex items-start gap-3" data-id="element-4180" data-mp="26ldohp76expv">
          <span className="w-9 h-9 rounded-lg bg-indigo-50 text-[#4F46E5] flex items-center justify-center flex-shrink-0" data-id="element-4181" data-mp="2bj6xepzv65tu">
            <BracesIcon className="w-4 h-4" data-id="element-4182" data-mp="2fkinsxqkav33" />
          </span>
          <p className="text-sm text-slate-600 leading-relaxed" data-id="element-4183" data-mp="2fixrp3oln434">
            {t('Personnalisez le contenu de vos avis avec des variables remplacées à l’envoi.', 'Personalize your notices with variables replaced at sending time.')}
            <br data-id="element-4184" data-mp="1rtd9wo46mcvf" />
            {t('Insérez-les avec le bouton', 'Insert them with the')}{' '}
            <span className="inline-flex items-center align-middle px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-600" data-id="element-4185" data-mp="1kb5cr2ofh6po">
              {'{ }'}
            </span>
          </p>
        </div>

        {/* Custom variables */}
        <div data-id="element-4186" data-mp="16cp43670964w">
          <div className="space-y-2" data-id="element-4187" data-mp="16b4apjwfft8m">
            {customVariables.map(variable => {
            const usage = getVariableUsage(variable.id, channels, lang);
            const isBlocked = blocked === variable.id;
            const missing = !variable.value[contentLang].trim() && usage.count > 0;
            return <div key={variable.id} className={`rounded-lg border px-3 py-2.5 ${isBlocked ? 'border-amber-300 bg-amber-50/60' : missing ? 'border-amber-200' : 'border-slate-200'}`} data-id="element-4188" data-mp="rkx35z7yjump">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3" data-id="element-4189" data-mp="2dqstq86stloj">
                    <div className="flex items-center gap-2.5 min-w-0 lg:w-64" data-id="element-4190" data-mp="2inja6wmimjah">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#4F46E5] text-white text-xs font-semibold truncate" data-id="element-4191" data-mp="2406l4xxi1vfc">
                        {variable.label[lang]}
                      </span>
                      {usage.count > 0 ? <span className="text-[11px] text-slate-400 truncate" data-id="element-4192" data-mp="1v12yxl31czwm">
                          {t('utilisée par ', 'used by ')}
                          <span className="font-medium text-slate-500" data-id="element-4193" data-mp="1l1c9hxtc1b3e">
                            {usage.channels.join(', ')}
                          </span>
                        </span> : <span className="text-[11px] text-slate-300" data-id="element-4194" data-mp="3ffv65jr70kdi">
                          {t('non utilisée', 'unused')}
                        </span>}
                    </div>

                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-4195" data-mp="18rctyp4kt5il">
                      {(['fr', 'en'] as const).map(valueLang => <div key={valueLang} className="relative" data-id="element-4196" data-mp="1ig4ewdb7ypk0">
                          <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase ${contentLang === valueLang ? 'text-[#4F46E5]' : 'text-slate-300'}`} data-id="element-4197" data-mp="1ihpdce0nmsn5">
                            {valueLang}
                          </span>
                          <input type="text" value={variable.value[valueLang]} onChange={e => patch(variable.id, {
                      value: {
                        ...variable.value,
                        [valueLang]: e.target.value
                      }
                    })} aria-label={`${variable.label[lang]} — ${valueLang.toUpperCase()}`} placeholder={valueLang === 'fr' ? 'Valeur en français…' : 'Value in English…'} className={`w-full border rounded-md pl-8 pr-2 py-1.5 text-xs focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] ${contentLang === valueLang ? 'border-slate-300' : 'border-slate-200 bg-slate-50/50'}`} data-id="element-4198" data-mp="1wgf9nrfngoag" />
                        </div>)}
                    </div>

                    <button type="button" onClick={() => removeVariable(variable)} aria-label={t('Supprimer la variable', 'Delete variable')} className="self-end lg:self-auto p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0" data-id="element-4199" data-mp="3iwhjezvzwbzk">
                      <TrashIcon className="w-4 h-4" data-id="element-4200" data-mp="5hv29t7h73zr" />
                    </button>
                  </div>

                  {isBlocked && <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700" data-id="element-4201" data-mp="jt92fgc5oak">
                      <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-4202" data-mp="3rwnvqifyj5x5" />
                      {t('Utilisée par ', 'Used by ')}
                      <span className="font-semibold" data-id="element-4203" data-mp="3mw9yzwjt3ief">
                        {usage.channels.join(', ')}
                      </span>
                      {t(' — retirez-la du contenu avant de la supprimer.', ' — remove it from the content before deleting it.')}
                    </p>}
                </div>;
          })}
          </div>

          {/* Creation row */}
          <AnimatePresence initial={false} data-id="element-4204" data-mp="3f0282iheazpm">
            {creating ? <motion.form key="create" initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.18
          }} onSubmit={createVariable} className="overflow-hidden" data-id="element-4205" data-mp="3jx2cyq2idwf5">
                <div className="mt-2 rounded-lg border border-[#4F46E5]/40 bg-indigo-50/40 p-3 space-y-3" data-id="element-4206" data-mp="1m8mrhqaqwt6">
                  <div className="flex flex-col lg:flex-row gap-3" data-id="element-4207" data-mp="2ma3ec9s9aeem">
                    <input type="text" autoFocus required value={draftLabel} onChange={e => setDraftLabel(e.target.value)} placeholder={t('Nom de la variable — ex. : Durée', 'Variable name — e.g. Duration')} aria-label={t('Nom de la variable', 'Variable name')} className="lg:w-64 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:border-[#4F46E5]" data-id="element-4208" data-mp="2m8sayb2q38o1" />
                    <input type="text" value={draftFr} onChange={e => setDraftFr(e.target.value)} placeholder={t('Valeur FR — ex. : 48 heures', 'FR value — e.g. 48 heures')} aria-label={t('Valeur en français', 'French value')} className="flex-1 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:border-[#4F46E5]" data-id="element-4209" data-mp="1w148zq493nw2" />
                    <input type="text" value={draftEn} onChange={e => setDraftEn(e.target.value)} placeholder={t('Valeur EN — ex. : 48 hours', 'EN value — e.g. 48 hours')} aria-label={t('Valeur en anglais', 'English value')} className="flex-1 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:border-[#4F46E5]" data-id="element-4210" data-mp="1ptr1bkhcdcdt" />
                  </div>
                  <div className="flex items-center gap-2 justify-end" data-id="element-4211" data-mp="123el7mby09c5">
                    <button type="button" onClick={() => setCreating(false)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors" data-id="element-4212" data-mp="x766ub5kkk61">
                      <XIcon className="w-3.5 h-3.5" data-id="element-4213" data-mp="j7y63dtrd3zq" />
                      {t('Annuler', 'Cancel')}
                    </button>
                    <button type="submit" disabled={!draftLabel.trim()} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[#4F46E5] rounded-md hover:bg-[#4338CA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-4214" data-mp="2uw34v8i3fbg4">
                      <CheckIcon className="w-3.5 h-3.5" data-id="element-4215" data-mp="2c4ipj86z0zkn" />
                      {t('Créer la variable', 'Create variable')}
                    </button>
                  </div>
                </div>
              </motion.form> : <motion.div key="add" initial={false} data-id="element-4216" data-mp="2l2ch65tsj51h">
                <button type="button" onClick={() => setCreating(true)} className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-[#4F46E5] bg-white border border-[#4F46E5] rounded-md hover:bg-indigo-50 transition-colors" data-id="element-4217" data-mp="2pvxf5xvdvz0o">
                  <PlusIcon className="w-3.5 h-3.5" data-id="element-4218" data-mp="30nnv7n2sl1rv" />
                  {t('Nouvelle variable', 'New variable')}
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}